import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { fetchWithReauthToken, ReauthRequiredError } from "@/hooks/use-reauth";
import { apiDelete, apiFetch, apiPatch, apiPost } from "@/lib/api";
import { useSession } from "@/lib/auth-client";
import { payAppKeys } from "@/lib/query";

// ── Types ──

export interface BillingPeriod {
  id: string;
  orgId: string;
  periodStart: string;
  periodEnd: string;
  status: string;
  createdAt: string;
}

export interface EnrichedSubLineItem {
  id: string;
  payApplicationId: string;
  commitmentLineItemId: string;
  projectId: string;
  itemNo: string;
  description: string;
  scheduledValue: number;
  prevApplications: number;
  completedThisPeriod: number;
  storedMaterials: number;
  totalCompletedStored: number;
  totalCompletedPct: number;
  balanceToFinish: number;
  retainage: number;
  costCodeId: string | null;
  costCodeCode: string | null;
  costCodeDescription: string | null;
}

export interface SubPayApp {
  id: string;
  orgId: string;
  projectId: string;
  commitmentId: string;
  payAppNumber: number;
  payAppType: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  status: string;
  retentionPercent: number;
  complianceGatePassed: boolean;
  lienWaiverReceived: boolean;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedByUserId: string | null;
  submittedByName: string | null;
  createdAt: string;
  updatedAt: string;
  // Commitment info
  commitmentNumber: string;
  vendor: string;
  companyId: string | null;
  scopeDescription: string | null;
  projectName: string;
  // G702 derived fields
  originalContractSum: number;
  approvedChangeOrders: number;
  contractSumToDate: number;
  totalCompletedStored: number;
  retainageAmount: number;
  totalEarnedLessRetainage: number;
  lessPreviousCertificates: number;
  currentPaymentDue: number;
  balanceToFinishIncRetainage: number;
  // Line items
  lineItems: EnrichedSubLineItem[];
  // Attachments
  attachmentCount: number;
}

export interface OwnerPayAppLineItem {
  id: string;
  payAppId: string;
  itemNo: string | null;
  name: string | null;
  description: string | null;
  scheduledValue: number;
  adjustmentToSchedule: number;
  approvedChangeOrders: number;
  revisedScheduledValue: number;
  prevApplications: number;
  completedThisPeriod: number;
  storedMaterials: number;
  totalCompletedStored: number;
  totalCompletedPct: number;
  balanceToFinish: number;
  retainage: number;
  costCodeId: string | null;
  lineType: "sub_sourced" | "gc_direct";
  scheduledValueLocked: boolean;
  retentionPercent: string | null;
  csiDivision: string | null;
  costCodeCode: string | null;
}

export interface OwnerPayAppAttachment {
  id: string;
  payAppId: string;
  payAppType: string;
  fileName: string;
  fileSize: number;
  mimeType: string | null;
  storagePath: string | null;
  uploadedBy: string | null;
  uploadedDate: string | null;
  createdAt: string;
}

export interface OwnerPayApp {
  id: string;
  direction: string;
  projectId: string;
  billingPeriodId: string | null;
  commitmentId: string | null;
  applicationNo: number;
  description: string | null;
  status: string;
  contractor: string | null;
  periodEnd: string | null;
  submittedDate: string | null;
  certifiedDate: string | null;
  amountCertified: string | null;
  rejectionReason: string | null;
  generatedPdfPath: string | null;
  createdAt: string;
  // Project info
  projectName: string;
  projectNumber: string | null;
  // G702 derived fields
  originalContractSum: number;
  approvedChangeOrders: number;
  contractSumToDate: number;
  retainagePct: number;
  totalCompletedStored: number;
  retainageAmount: number;
  totalEarnedLessRetainage: number;
  lessPreviousCertificates: number;
  currentPaymentDue: number;
  balanceToFinishIncRetainage: number;
  // Line items & attachments
  lineItems: OwnerPayAppLineItem[];
  attachments: OwnerPayAppAttachment[];
  attachmentCount: number;
  // Distribution flags
  distribOwner: boolean;
  distribDesigner: boolean;
  distribContractor: boolean;
}

export interface OwnerContract {
  id: string;
  projectId: string;
  projectName: string | null;
  commitmentNumber: string;
  title: string;
  originalContractSum: string | null;
  approvedChangeOrders: string | null;
  contractSumToDate: string | null;
  retainagePct: string | null;
  status: string;
}

export interface SubBreakdownContribution {
  subPayAppId: string;
  subPayAppNo: number;
  subCompanyName: string;
  subLineDescription: string;
  completedThisPeriod: number;
  storedMaterials: number;
  totalAmount: number;
  approvalStatus: string;
  commitmentNumber: string | null;
  commitmentTitle: string | null;
  costCodeCode: string | null;
  billingPeriodStart: string | null;
  billingPeriodEnd: string | null;
  approverName: string | null;
  approvedAt: string | null;
  sourceSystem: "legacy" | "normalized";
}

export interface SubBreakdownEntry {
  ownerLineId: string;
  ownerItemNo: string;
  ownerDescription: string;
  ownerCostCodeId: string | null;
  lineType: "sub_sourced" | "gc_direct";
  contributions: SubBreakdownContribution[];
}

export interface RefreshDiffItem {
  lineId: string;
  costCodeId: string | null;
  description: string;
  current: {
    completedThisPeriod: number;
    prevApplications: number;
    approvedChangeOrders: number;
  };
  proposed: {
    completedThisPeriod: number;
    prevApplications: number;
    approvedChangeOrders: number;
  };
}

export interface GcDirectLineItemInput {
  description: string;
  scheduledValue: string;
  adjustmentToSchedule?: string;
  approvedChangeOrders?: string;
  completedThisPeriod?: string;
  storedMaterials?: string;
  costCodeId?: string;
  retentionPercent?: string;
}

export interface GcDirectHistoryEntry {
  id: string;
  action: string;
  actorName: string | null;
  previousState: Record<string, unknown> | null;
  newState: Record<string, unknown> | null;
  changedFields: string[] | null;
  createdAt: string;
}

export interface ComplianceCheckResult {
  compliancePass: boolean;
  issues: Array<{ document: string; status: string; message: string }>;
  lienWaiverRequired: boolean;
  vendor: string;
  checkedAt: string;
}

export interface LienWaiverAttachment {
  id: string;
  payAppId: string;
  payAppType: string;
  fileName: string;
  fileSize: number;
  mimeType: string | null;
  uploadedBy: string | null;
  uploadedDate: string | null;
}

// ── Billing Periods ──

export function useBillingPeriods() {
  return useQuery({
    queryKey: payAppKeys.billingPeriods,
    queryFn: () => apiFetch<BillingPeriod[]>("/billing-periods"),
  });
}

export function useCreateBillingPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { periodStart: string; periodEnd: string; status?: string }) =>
      apiPost<{ data: BillingPeriod }>("/billing-periods", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.billingPeriods });
      toast.success("Billing period created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateBillingPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      periodStart?: string;
      periodEnd?: string;
      status?: string;
    }) => apiPatch<{ data: BillingPeriod }>(`/billing-periods/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.billingPeriods });
      toast.success("Billing period updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteBillingPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/billing-periods/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.billingPeriods });
      toast.success("Billing period deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Subcontract Pay Applications ──

export function useSubPayApps(filters?: { projectId?: string }) {
  const { data: session } = useSession();
  const isContractor = session?.user?.role === "contractor";

  // Contractors without a project filter should see only pay apps tied to their
  // own company — route to the company-scoped endpoint. PM/admin (and any caller
  // that passes an explicit projectId filter) continue to use the project query.
  const useCompanyScope = isContractor && !filters?.projectId;

  const params = new URLSearchParams();
  if (filters?.projectId) params.set("projectId", filters.projectId);
  const qs = params.toString();

  return useQuery({
    queryKey: [...payAppKeys.sub, { ...filters, scope: useCompanyScope ? "company" : "project" }],
    queryFn: () =>
      apiFetch<SubPayApp[]>(
        useCompanyScope
          ? "/pay-applications/subcontract/company"
          : `/pay-applications/subcontract${qs ? `?${qs}` : ""}`,
      ),
  });
}

export function useSubPayApp(id: string) {
  return useQuery({
    queryKey: payAppKeys.subDetail(id),
    queryFn: () => apiFetch<SubPayApp>(`/pay-applications/subcontract/${id}`),
    enabled: !!id,
  });
}

export function useCreateSubPayApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiPost<{ data: SubPayApp }>("/pay-applications/subcontract", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.sub });
      toast.success("Pay application created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateSubPayAppLineItems(payAppId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { lineItems: Record<string, unknown>[] }) =>
      apiPatch<{ data: SubPayApp }>(`/pay-applications/subcontract/${payAppId}/line-items`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.sub });
      qc.invalidateQueries({ queryKey: payAppKeys.subDetail(payAppId) });
      toast.success("Line items updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useSubmitSubPayApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<{ data: SubPayApp }>(`/pay-applications/subcontract/${id}/submit`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.sub });
      toast.success("Pay application submitted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useApproveSubPayApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPatch<{ data: SubPayApp }>(`/pay-applications/subcontract/${id}/approve`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.sub });
      toast.success("Pay application approved");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useRejectSubPayApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiPatch<{ data: SubPayApp }>(`/pay-applications/subcontract/${id}/reject`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.sub });
      toast.success("Pay application rejected");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useReturnSubPayAppForRevision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<{ data: SubPayApp }>(`/pay-applications/subcontract/${id}/return-for-revision`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.sub });
      toast.success("Pay application returned for revision");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteSubPayApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/pay-applications/subcontract/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.sub });
      toast.success("Pay application deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Lien Waivers ──

export function useLienWaivers(payAppId: string) {
  return useQuery({
    queryKey: ["lien-waivers", payAppId],
    queryFn: () =>
      apiFetch<LienWaiverAttachment[]>(`/pay-applications/subcontract/${payAppId}/lien-waiver`),
    enabled: !!payAppId,
  });
}

export function useUploadLienWaiver(payAppId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return fetch(`/api/pay-applications/subcontract/${payAppId}/lien-waiver`, {
        method: "POST",
        body: formData,
        credentials: "include",
      }).then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as Record<string, string>).error ?? "Upload failed");
        }
        return res.json();
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lien-waivers", payAppId] });
      qc.invalidateQueries({ queryKey: payAppKeys.subDetail(payAppId) });
      toast.success("Lien waiver uploaded");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Compliance Check ──

export function useComplianceCheck(commitmentId: string) {
  return useQuery({
    queryKey: ["compliance-check", commitmentId],
    queryFn: () => apiFetch<ComplianceCheckResult>(`/sub-portal/compliance-check/${commitmentId}`),
    enabled: !!commitmentId,
  });
}

// ── Owner Pay Applications ──

export function useOwnerPayApps(filters?: { projectId?: string }) {
  const params = new URLSearchParams();
  if (filters?.projectId) params.set("projectId", filters.projectId);
  const qs = params.toString();

  return useQuery({
    queryKey: [...payAppKeys.owner, filters],
    queryFn: () => apiFetch<OwnerPayApp[]>(`/pay-applications/owner${qs ? `?${qs}` : ""}`),
  });
}

export function useOwnerPayApp(id: string) {
  return useQuery({
    queryKey: payAppKeys.ownerDetail(id),
    queryFn: () => apiFetch<OwnerPayApp>(`/pay-applications/owner/${id}`),
    enabled: !!id,
  });
}

export function useCreateOwnerPayApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiPost<{ data: OwnerPayApp }>("/pay-applications/owner", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.owner });
      toast.success("Owner pay application created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateOwnerPayApp(payAppId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      distribOwner?: boolean;
      distribDesigner?: boolean;
      distribContractor?: boolean;
    }) => apiPatch<{ data: OwnerPayApp }>(`/pay-applications/owner/${payAppId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.owner });
      qc.invalidateQueries({ queryKey: payAppKeys.ownerDetail(payAppId) });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateOwnerPayAppLineItems(payAppId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { lineItems: Record<string, unknown>[] }) =>
      apiPatch<{ data: OwnerPayApp }>(`/pay-applications/owner/${payAppId}/line-items`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.owner });
      qc.invalidateQueries({
        queryKey: payAppKeys.ownerDetail(payAppId),
      });
      toast.success("Line items updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useSubmitOwnerPayApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<{ data: OwnerPayApp }>(`/pay-applications/owner/${id}/submit`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.owner });
      toast.success("Owner pay application submitted");
    },
    onError: (err: Error) => {
      if (err.message === "UNAPPROVED_SUB_PAY_APPS") {
        toast.error("Cannot submit — some contributing sub pay apps are not yet approved.");
      } else {
        toast.error(err.message);
      }
    },
  });
}

export function useCertifyOwnerPayApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reauthToken }: { id: string; reauthToken: string }) =>
      fetchWithReauthToken<{ data: OwnerPayApp }>(
        `/api/pay-applications/owner/${id}/certify`,
        "POST",
        {},
        reauthToken,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.owner });
      toast.success("Owner pay application certified");
    },
    onError: (err: Error) => {
      if (err instanceof ReauthRequiredError) return;
      toast.error(err.message);
    },
  });
}

export function useRejectOwnerPayApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      apiPost<{ data: OwnerPayApp }>(`/pay-applications/owner/${id}/reject`, {
        reason,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.owner });
      toast.success("Owner pay application rejected");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteOwnerPayApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/pay-applications/owner/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.owner });
      toast.success("Owner pay application deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useOwnerPayAppRefreshPreview(payAppId: string, enabled: boolean) {
  return useQuery({
    queryKey: payAppKeys.ownerRefreshPreview(payAppId),
    queryFn: () =>
      apiPost<{ preview: RefreshDiffItem[] }>(
        `/pay-applications/owner/${payAppId}/refresh-from-subs?preview=true`,
      ),
    enabled: enabled && !!payAppId,
    staleTime: 0,
  });
}

export function useRefreshOwnerPayAppFromSubs(payAppId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<OwnerPayApp>(`/pay-applications/owner/${payAppId}/refresh-from-subs`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.owner });
      qc.invalidateQueries({ queryKey: payAppKeys.ownerDetail(payAppId) });
      qc.invalidateQueries({ queryKey: payAppKeys.ownerSubBreakdown(payAppId) });
      toast.success("Line items refreshed from subcontractor pay apps");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Owner Pay App Sub Breakdown ──

export function useOwnerPayAppSubBreakdown(id: string) {
  return useQuery({
    queryKey: payAppKeys.ownerSubBreakdown(id),
    queryFn: () => apiFetch<SubBreakdownEntry[]>(`/pay-applications/owner/${id}/sub-breakdown`),
    enabled: !!id,
  });
}

// ── GC Direct Cost Line Items ──

export function useCreateGcDirectLineItem(payAppId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GcDirectLineItemInput) =>
      apiPost<OwnerPayAppLineItem>(
        `/pay-applications/owner/${payAppId}/line-items/gc-direct`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.ownerDetail(payAppId) });
      qc.invalidateQueries({ queryKey: payAppKeys.owner });
      toast.success("GC Direct line item added");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateGcDirectLineItem(payAppId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      lineItemId,
      ...updates
    }: Partial<GcDirectLineItemInput> & { lineItemId: string }) =>
      apiPatch<OwnerPayAppLineItem>(
        `/pay-applications/owner/${payAppId}/line-items/${lineItemId}/gc-direct`,
        updates,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.ownerDetail(payAppId) });
      qc.invalidateQueries({ queryKey: payAppKeys.owner });
      toast.success("Line item updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteGcDirectLineItem(payAppId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineItemId: string) =>
      apiDelete(`/pay-applications/owner/${payAppId}/line-items/${lineItemId}/gc-direct`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.ownerDetail(payAppId) });
      qc.invalidateQueries({ queryKey: payAppKeys.owner });
      toast.success("Line item deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useGcDirectHistory(payAppId: string, lineItemId?: string) {
  const params = lineItemId ? `?lineItemId=${lineItemId}` : "";
  return useQuery({
    queryKey: payAppKeys.ownerGcDirectHistory(payAppId, lineItemId),
    queryFn: () =>
      apiFetch<GcDirectHistoryEntry[]>(
        `/pay-applications/owner/${payAppId}/gc-direct-history${params}`,
      ),
    enabled: !!payAppId,
  });
}

export function useGenerateOwnerPayAppPdf(payAppId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost<{ pdfPath: string; attachmentId: string }>(
        `/pay-applications/owner/${payAppId}/generate-pdf`,
      ),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: payAppKeys.ownerDetail(payAppId) });
      toast.success("PDF generated — downloading…");
      const a = document.createElement("a");
      a.href = `/api/pay-applications/owner/${payAppId}/attachments/${data.attachmentId}`;
      a.download = "G702-G703.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── GR Refresh (WI-GR-05) ──

export interface GrRefreshDiffItem {
  lineId: string;
  itemNo: string | null;
  grTotal: number;
  prevTotal: number;
  proposed: number;
}

export function useOwnerPayAppGrRefreshPreview(payAppId: string, enabled: boolean) {
  return useQuery({
    queryKey: payAppKeys.ownerGrRefreshPreview(payAppId),
    queryFn: () =>
      apiPost<{ preview: GrRefreshDiffItem[] }>(
        `/pay-applications/owner/${payAppId}/refresh-from-grs?preview=true`,
      ),
    enabled: enabled && !!payAppId,
    staleTime: 0,
  });
}

export function useRefreshOwnerPayAppFromGRs(payAppId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<OwnerPayApp>(`/pay-applications/owner/${payAppId}/refresh-from-grs`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payAppKeys.owner });
      qc.invalidateQueries({ queryKey: payAppKeys.ownerDetail(payAppId) });
      toast.success("GR line items refreshed");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Owner Contracts ──

export function useOwnerContracts(filters?: { projectId?: string }) {
  const params = new URLSearchParams();
  if (filters?.projectId) params.set("projectId", filters.projectId);
  const qs = params.toString();

  return useQuery({
    queryKey: ["owner-contracts", filters],
    queryFn: () => apiFetch<OwnerContract[]>(`/owner-contracts${qs ? `?${qs}` : ""}`),
  });
}
